import * as yup from 'yup';
import { Formik, FieldArray } from 'formik';
import React, { useContext, useState } from 'react';
import { navigate } from 'gatsby';
import { CardNumberElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js';

import DonationAmountForm from './DonationAmountForm';
import BillingInformationForm from './BIllingInformationForm';
import { AppContext } from '../../../apollo/wrap-root-element';
import DeleteProjectDonationModel from './DeleteProjectDonationModel';
import DonateToProjectFailedModal from './DonateToProjectFailedModal';
import { ConnectedFocusError } from 'focus-formik-error';

import { isValidPhoneNumber } from 'libphonenumber-js';
import GiveDonateForm from '../../NewGive/DonateForm';

const CustomDonations = ({ projectsLists, defaultSelected }) => {
  const { currency, currenciesRate } = useContext(AppContext);
  const [activeStep, setActiveStep] = useState(0);
  const [serverError, setServerError] = useState('');
  const [selectedRate] = !!currenciesRate?.length && currenciesRate.filter((elm) => elm.currency === currency);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showProjectDonateFailedModal, setShowProjectDonateFailedModal] = useState(false);

  const { currentUserDetails } = React.useContext(AppContext);
  const [deletingProjectIndex, setDeletingProjectIndex] = useState(0);

  const validationSchema = yup.object({
    cardNumber: yup.bool().oneOf([true], 'Please enter your card number'),
    expiryDate: yup.bool().oneOf([true], 'Please enter expiry date'),
    cardHolderName: yup.string().required('CardHolder name is required'),
    cvc: yup.bool().oneOf([true], 'Please enter cvc'),
    firstName: yup.string().required('First name is required'),
    lastName: yup.string().required('Last name is required'),
    email: yup.string().required('Email is required '),
    country: yup.string().required('Country is required'),
    phone: yup
      .string()
      .transform((value, originalValue) => {
        // Strip out any non-numeric characters
        return originalValue;
      })
      .test('is-valid-phone', 'Invalid phone number', function (value) {
        if (!value) return this.createError({ message: 'Phone number is required' });

        const countryCode = this.resolve(yup.ref('country'));

        if (!countryCode) return this.createError({ message: 'Country code is required' });

        // Parse phone number using libphonenumber-js
        const isValid = isValidPhoneNumber(value, countryCode);

        // Check if the parsed phone number is valid
        if (!isValid) {
          return this.createError({ message: 'Invalid phone number' });
        }

        // Phone number is valid
        return true;
      }),
    addressLine1: yup.string().required('Address line 1 is required'),
    addressLine2: yup.string(),
    zipCode: yup.string().required('Postal code is required'),
    city: yup.string().required('City is required'),
    state: yup.string().required('State is required'),

    donateType: yup.string().required('Give type is required'),
    emailTaxReceipts: yup.boolean(),
    enableProcessingFee: yup.boolean(),
    amount: yup.string().required('Amount cannot be empty'),
    customAmount: yup.string(),
    projectID: yup.string(),

    donatedProjectsLists: yup.array().of(
      yup.object().shape({
        projectID: yup.string(),
        customAmount: yup.string(),
        donateType: yup.string().required('Give type is required'),
        amount: yup.string().required('Amount cannot be empty'),
      })
    ),
  });

  const initialValues = {
    cardNumber: false,
    expiryDate: false,
    cardHolderName: '',
    cvc: false,
    firstName: currentUserDetails?.customer?.firstName ? currentUserDetails.customer.firstName : '',
    lastName: currentUserDetails?.customer?.lastName ? currentUserDetails.customer.lastName : '',
    email: currentUserDetails?.customer?.email ? currentUserDetails.customer.email : '',
    country: 'US',
    phone: currentUserDetails?.customer?.phone ? currentUserDetails.customer.phone : '',
    addressLine1: '',
    addressLine2: '',
    zipCode: '',
    city: '',
    state: '',
    donateType: 'monthly',
    emailTaxReceipts: false,
    enableProcessingFee: false,
    shopifyUserId: '',
    amount: '10',
    projectID: defaultSelected.value,
    customAmount: '',
    donatedProjectsLists: [
      {
        projectID: defaultSelected.value,
        customAmount: '',
        amount: '10',
        donateType: 'monthly',
      },
    ],
  };

  const stripe = useStripe();
  const elements = useElements();
  const submitForm = (values) => {
    setServerError('');
    const cardNumberElement = elements?.getElement(CardNumberElement); // to catch cardnumber element input

    const donateAmount = values.customAmount ? +values.customAmount : +values.amount;

    const processingFeeCharge = (donateAmount * 2) / 100;
    const amountWithProcessingCharge = donateAmount + processingFeeCharge;

    const project = projectsLists.find((item) => item.value === values.donatedProjectsLists[0].projectID);
    const formData = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      address: {
        country: values.country,
        city: values.city,
        state: values.state,
        zipCode: values.zipCode,
        addressLine1: values.addressLine1,
        addressLine2: values.addressLine2,
      },
      phone: values.phone,
      donateType: values.donateType,
      emailTaxReceipts: values.emailTaxReceipts,
      enableProcessingFee: values.enableProcessingFee,
      amount: values.enableProcessingFee ? amountWithProcessingCharge : donateAmount,
      cardHolderName: values.cardHolderName,
      currency: currency.toLowerCase(),
      currencyRateToUSD: selectedRate.rate,
      donatedProjects: values.donatedProjectsLists.map((item) => {
        const project = projectsLists.find((pl) => pl.value === item.projectID);

        return {
          uid: project?.parentProjectId,
          name: project?.parentProjectName,
          subName: project?.childProjectName,
          subUid: project?.childProjectId,
          amount: item?.customAmount ? +item?.customAmount : +item?.amount,
          donateType: item.donateType,
        };
      }),
    };

    if (currentUserDetails?.customer?.id) {
      formData['shopifyUserId'] = currentUserDetails?.customer?.id;
    }
    setIsSubmitting(true);

    fetch(`${process.env.GATSBY_LINEAGE_BACKEND_URL}api/donate/donate-to-project`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...formData,
      }),
    })
      .then((resp) => resp.json())
      .then((body) => {
        if (body.success) {
          stripe
            .confirmCardPayment(body.data.clientSecret, {
              payment_method: { card: cardNumberElement },
            })
            .then(function (result) {
              if (result.error) {
                setShowProjectDonateFailedModal(true);
                // setServerError(result.error.message);
                // Inform the customer that there was an error.
                setIsSubmitting(false);
              } else {
                navigate('/success-donation');
              }
            });
        } else {
          setShowProjectDonateFailedModal(true);
          // setServerError("Payment couldn't proceed. Please try again later");
          setIsSubmitting(false);
        }
      })
      .catch((err) => {
        setShowProjectDonateFailedModal(true);
        // setServerError("Payment couldn't proceed. Please try again later");
        setIsSubmitting(false);
      });
  };

  const getProjectDetails = (selectedProjectId) => projectsLists?.find((pl) => pl.value === selectedProjectId);

  return (
    <div>
      <Formik
        enableReinitialize
        validateOnMount
        validationSchema={validationSchema}
        initialValues={initialValues}
        onSubmit={submitForm}>
        {(formik) => (
          //old below
          <div className="custom-donations-form pd-x-lg ">
            <DonateToProjectFailedModal
              open={showProjectDonateFailedModal}
              onClose={() => setShowProjectDonateFailedModal(false)}
              onConfirmDelete={() => setShowProjectDonateFailedModal(false)}
            />
            <form onSubmit={formik.handleSubmit}>
              <ConnectedFocusError />
              {activeStep === 0 && (
                <FieldArray
                  name="donatedProjectsLists"
                  render={(arrayHelpers) => (
                    <>
                    {/* <GiveDonateForm
                     setActiveStep={setActiveStep}
                     projectsLists={projectsLists}
                     defaultSelected={defaultSelected}
                     formik={formik}
                     arrayHelpers={arrayHelpers}
                     onDeleteDonationProject={(index) => {
                       setDeletingProjectIndex(index);
                       setShowDeleteModal(true);
                     }}
                    /> */}
                      <DonationAmountForm
                        setActiveStep={setActiveStep}
                        projectsLists={projectsLists}
                        defaultSelected={defaultSelected}
                        formik={formik}
                        arrayHelpers={arrayHelpers}
                        onDeleteDonationProject={(index) => {
                          setDeletingProjectIndex(index);
                          setShowDeleteModal(true);
                        }}
                      />
                      <DeleteProjectDonationModel
                        open={showDeleteModal}
                        onClose={() => setShowDeleteModal(false)}
                        onConfirmDelete={() => arrayHelpers.remove(deletingProjectIndex)}
                        projectName={
                          getProjectDetails(formik.values.donatedProjectsLists[deletingProjectIndex].projectID).label
                        }
                      />
                    </>
                  )}
                />
              )}

              {activeStep === 1 && (
                <BillingInformationForm
                  serverError={serverError}
                  setActiveStep={setActiveStep}
                  isSubmitting={isSubmitting}
                  formik={formik}
                />
              )}
            </form>
          </div>
        )}
      </Formik>
    </div>
  );
};

export default CustomDonations;
